import { useCallback, useEffect, useState } from 'react';
import {
  type AlignmentFlag,
  type AlignmentRulingValue,
  ApiError,
  getAlignmentReview,
  ruleAlignmentFlag,
} from './api';
import {
  INITIAL_ALIGNMENT_REVIEW_RULING_STATE,
  applyOptimisticRuling,
  confirmRuling,
  rollbackRuling,
} from './alignmentReview';

/**
 * The alignment-review deck's data hook — read-only fetch of the open flags
 * (mirrors useDnaOverlay's fetch-on-mount shape) PLUS the one write action a
 * human ruling drives (mirrors useCorpusCanvas's `rehome` optimistic
 * discipline: apply -> server call -> confirm | rollback).
 *
 * THE GOVERNING LAW: every `rule()` call is ONE explicit human decision on
 * ONE card — this hook never batches, never auto-applies, never re-derives a
 * ruling from anything but the caller's own tap. A ruling is TERMINAL: once
 * the server confirms, the achievementId is gone from `state.flags` for the
 * rest of this session (and will not resurface on a future refresh() either,
 * since the server itself excludes any achievementId with a ruling row).
 *
 * G1 (docs/CHANGE-CONTROL.md R184) — the whole alignment-review surface now
 * gates under the 'tailoring' capability (alignment.routes.ts). A free-tier
 * 402 is a DISTINCT, honest state (`entitlementBlock`) — never folded into
 * the generic `error` string, mirroring JobDetail.tsx's own entitlementBlock
 * parsing byte-for-byte (same served tier/reason/hint shape, same
 * `entitlement_required` discriminator) so the deck can render the SAME
 * honest "why you're blocked" note the tailor path already does, never a
 * silent absence standing in for a real paid-feature gate.
 */
export interface AlignmentReviewEntitlementBlock {
  tier: string;
  reason: string;
  hint: string;
}

export interface AlignmentReviewState {
  flags: AlignmentFlag[];
  loading: boolean;
  error: string | null;
  /** Present only when the LAST fetch was blocked by requireEntitlement('tailoring')
   *  (a 402 entitlement_required response) — null in every other case,
   *  including a genuine network/server error (which surfaces via `error`
   *  instead, never both at once). */
  entitlementBlock: AlignmentReviewEntitlementBlock | null;
}

/** The refresh() catch branch's decision, made pure and exported — same
 *  extraction discipline useDna.ts's lastQuestionOf documents (this repo has
 *  no jsdom/@testing-library/react anywhere, so a hook itself cannot be
 *  rendered/tested here; this is the one piece of refresh()'s logic worth
 *  pulling out and unit-testing directly rather than only exercising it
 *  end-to-end through the live route).
 *
 * Given whatever refresh() caught, returns EITHER the entitlement block to
 * set (a 402 entitlement_required body, tier/reason/hint defaulted exactly
 * like JobDetail.tsx's handleTailor catch branch) OR the generic error
 * string to set — never both, mirroring AlignmentReviewState's own
 * mutual-exclusion contract on those two fields. */
export function classifyAlignmentReviewFetchError(
  err: unknown,
): { entitlementBlock: AlignmentReviewEntitlementBlock; error: null } | { entitlementBlock: null; error: string } {
  if (err instanceof ApiError && err.status === 402 && err.body && typeof err.body === 'object') {
    const body = err.body as { error?: string; tier?: string; reason?: string; hint?: string };
    if (body.error === 'entitlement_required') {
      return {
        entitlementBlock: {
          tier: body.tier ?? 'free',
          reason: body.reason ?? 'no_subscription',
          hint: body.hint ?? 'start your trial',
        },
        error: null,
      };
    }
  }
  return {
    entitlementBlock: null,
    error: err instanceof Error ? err.message : String(err),
  };
}

export function useAlignmentReview() {
  const [state, setState] = useState<AlignmentReviewState>({
    flags: [],
    loading: true,
    error: null,
    entitlementBlock: null,
  });
  const [rulingState, setRulingState] = useState(INITIAL_ALIGNMENT_REVIEW_RULING_STATE);

  const refresh = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null, entitlementBlock: null }));
    try {
      const { flags } = await getAlignmentReview();
      setState({ flags, loading: false, error: null, entitlementBlock: null });
    } catch (err) {
      // Mirrors JobDetail.tsx's handleTailor catch branch exactly: a 402
      // entitlement_required body renders as the honest blocked state, never
      // as a generic error string a "try again" affordance would imply is
      // retriable in the same way a network hiccup is. The classification
      // itself is pure + exported (classifyAlignmentReviewFetchError above)
      // so it is unit-tested directly rather than only through this effect.
      const classified = classifyAlignmentReviewFetchError(err);
      setState({ flags: [], loading: false, ...classified });
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  /**
   * Rule on one flagged achievement. Optimistically removes it from the
   * visible deck (a ruling is terminal — the card leaves immediately,
   * regardless of which of the four rulings the user picked), fires the
   * POST, and either confirms (snapshot dropped) or rolls back (the flag
   * reappears) on failure. Returns an error string on failure (null on
   * success) so the caller can surface it inline, mirroring
   * useCorpusCanvas's rehome/editRole/editAchievement return contract.
   *
   * `mergeIntoAchievementId` (RESUME-POLISH lane, part c): required when
   * ruling === 'merged' — the survivor id the deck computed via the SAME
   * richest-representative pick the résumé composer uses. A 'merged' ruling
   * on a near_duplicate cluster's OTHER members is a SEPARATE tap each (a
   * user merging 2 of 3 cluster members calls rule() twice, once per card —
   * this hook's own "one card at a time" pending-state discipline already
   * enforces that no two rulings race).
   */
  const rule = useCallback(
    async (
      achievementId: string,
      ruling: AlignmentRulingValue,
      movedToRoleId?: string,
      mergeIntoAchievementId?: string,
    ): Promise<string | null> => {
      const target = state.flags.find((f) => f.achievementId === achievementId);
      if (!target) return null;

      const { next, state: nextRulingState } = applyOptimisticRuling(state.flags, achievementId);
      setState((s) => ({ ...s, flags: next }));
      setRulingState(nextRulingState);

      try {
        await ruleAlignmentFlag(achievementId, {
          ruling,
          movedToRoleId,
          mergeIntoAchievementId,
          // The EXACT evidence the user was shown — frozen into the ruling
          // ledger verbatim (never regenerated from a live re-run later).
          evidenceSnapshot: target.signals,
        });
        setRulingState(confirmRuling());
      } catch (err) {
        const { restored } = rollbackRuling(nextRulingState);
        setRulingState(INITIAL_ALIGNMENT_REVIEW_RULING_STATE);
        if (restored) setState((s) => ({ ...s, flags: restored }));
        return err instanceof Error ? err.message : 'Could not save that — try again.';
      }
      return null;
    },
    [state.flags],
  );

  return { state, refresh, rule, rulingState };
}
